import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize, Translate } from 'react-localize-redux';
import { Box, Typography } from '@mui/material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import Dialog from '../../../components/widgets/MuiDialog';
import css from './ReorderValuesDialog.css';
import Button from '../../../components/atoms/Button';
import DragDropElement from '../../../components/organisms/DragDropElement';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';

const ReorderValuesDialog = ({
    customDimensionName = '',
    values = [],
    valuesIds = [],
    id = '',
    onValueReordered = () => {},
    onDone = () => {},
    ...props
}) => {
    const [reorderedValues, setReorderedValues] = useState(values);
    const reorderedValuesIds = useRef(valuesIds);
    const disableSave = reorderedValuesIds.current?.toString() === valuesIds?.toString();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback(
        event => {
            const { active, over } = event;

            if (active.id !== over.id) {
                const oldIndex = reorderedValues.indexOf(active.id);
                const newIndex = reorderedValues.indexOf(over.id);

                setReorderedValues(items => {
                    const newItems = arrayMove(items, oldIndex, newIndex);
                    if (reorderedValuesIds.current) {
                        reorderedValuesIds.current = arrayMove([...reorderedValuesIds.current], oldIndex, newIndex);
                    }
                    return newItems;
                });
            }
        },
        [reorderedValues]
    );

    const onSave = useCallback(() => {
        if (reorderedValuesIds.current?.toString() !== valuesIds?.toString()) {
            onValueReordered(id, reorderedValuesIds.current);
            onDone();
            props.onClose();
        }
    }, [id, valuesIds, onValueReordered, onDone, props.onClose]);

    return (
        <Dialog title="STATIC.PAGES.CUSTOM_DIMENSIONS.REORDER_VALUES.DIALOG_TITLE" {...props} paperClass={css.dialog}>
            <Box className={css.content}>
                <Box className={css.descriptionContainer}>
                    <Icon
                        name="info-circle-filled"
                        size={IconSizes.X_LARGE}
                        variant={IconVariants.ACTIVE}
                        interactive={false}
                        className={css.infoIcon}
                    />
                    <Translate id="STATIC.PAGES.CUSTOM_DIMENSIONS.REORDER_VALUES.DIALOG_TEXT" />
                </Box>
                <Box className={css.valuesContainer}>
                    <Typography className={css.customDimensionName}>
                        {customDimensionName} ({values.length} values)
                    </Typography>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={reorderedValues} strategy={verticalListSortingStrategy}>
                            <div className={css.dragDropContainer}>
                                {reorderedValues.map((value, index) => (
                                    <DragDropElement key={value} index={index} value={value} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </Box>
                <Box className={css.footerButtonContainer}>
                    <Button type="flat" onClick={props.onClose}>
                        <Translate id="STATIC.BUTTONS.CANCEL" />
                    </Button>
                    <Button disabled={disableSave} type="primary" level="level1" onClick={onSave}>
                        <Translate id="STATIC.BUTTONS.DONE" />
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

ReorderValuesDialog.propTypes = {
    customDimensionName: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.string),
    valuesIds: PropTypes.arrayOf(PropTypes.string),
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValueReordered: PropTypes.func,
    id: PropTypes.string,
    onDone: PropTypes.func,
};

export default withLocalize(ReorderValuesDialog);
